import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Upload, DollarSign, FileText, RefreshCw, Cloud, CloudOff, LogOut, User, Package, Plus, Trash2, Search, Archive } from 'lucide-react';
import * as GS from './googleService';

/* ═══════════════════════════════════════════
   LOGIN SCREEN
   ═══════════════════════════════════════════ */
const LoginScreen = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [gapiReady, setGapiReady] = useState(false);

  useEffect(() => {
    // Initialize GAPI when component mounts
    const init = async () => {
      const ready = await GS.initGapi();
      setGapiReady(ready);
    };
    if (window.gapi) { init(); }
    else { 
      // Wait for GAPI script to load
      const interval = setInterval(() => {
        if (window.gapi) { clearInterval(interval); init(); }
      }, 200);
      return () => clearInterval(interval);
      
    }
  }, []);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      if (window.google?.accounts?.oauth2 && gapiReady) {
        const user = await GS.loginWithGoogle();
        onLogin(user);
      } else {
        // Demo mode if Google APIs not configured
        setTimeout(() => {
          onLogin({ name: 'Usuario Demo', email: 'demo@fondapp.cl', picture: null, token: 'demo' });
          setIsLoading(false);
        }, 1200);
        return;
      }
    } catch (err) {
      setError('Error al conectar con Google: ' + (err.message || 'Intenta nuevamente'));
    }
    setIsLoading(false);
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#0f172a,#1e293b 40%,#334155 70%,#1e293b)', fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ position:'fixed', inset:0, overflow:'hidden', pointerEvents:'none', zIndex:0 }}>
        <div style={{ position:'absolute', top:'-20%', right:'-10%', width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle,rgba(99,102,241,.15),transparent 70%)' }}/>
        <div style={{ position:'absolute', bottom:'-15%', left:'-5%', width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle,rgba(16,185,129,.1),transparent 70%)' }}/>
        <div style={{ position:'absolute', inset:0, opacity:.03, backgroundImage:'linear-gradient(rgba(255,255,255,.1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.1) 1px,transparent 1px)', backgroundSize:'60px 60px' }}/>
      </div>
      <div style={{ position:'relative', zIndex:1, width:'100%', maxWidth:460, padding:'0 20px' }}>
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:80, height:80, borderRadius:20, background:'linear-gradient(135deg,#6366f1,#8b5cf6)', boxShadow:'0 20px 40px rgba(99,102,241,.3)', marginBottom:24 }}>
            <DollarSign size={40} color="white"/>
          </div>
          <h1 style={{ fontSize:42, fontWeight:800, color:'#f8fafc', letterSpacing:'-1.5px', margin:'0 0 8px' }}>FondApp</h1>
          <p style={{ fontSize:16, color:'#94a3b8', fontWeight:400 }}>Sistema de Gestión de Fondos Concursables</p>
        </div>
        <div style={{ background:'rgba(30,41,59,.8)', backdropFilter:'blur(20px)', borderRadius:24, border:'1px solid rgba(148,163,184,.1)', padding:'40px 36px', boxShadow:'0 25px 50px rgba(0,0,0,.3)' }}>
          <h2 style={{ fontSize:22, fontWeight:700, color:'#e2e8f0', textAlign:'center', marginBottom:8 }}>Iniciar Sesión</h2>
          <p style={{ fontSize:14, color:'#64748b', textAlign:'center', marginBottom:32 }}>Conecta tu cuenta de Google para sincronizar con Sheets y Drive</p>
          {error && <div style={{ background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.3)', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10 }}><AlertCircle size={18} color="#ef4444"/><span style={{ color:'#fca5a5', fontSize:13 }}>{error}</span></div>}
          <button onClick={handleGoogleLogin} disabled={isLoading} style={{ width:'100%', padding:'14px 24px', background:isLoading?'rgba(255,255,255,.05)':'#fff', color:isLoading?'#94a3b8':'#1f2937', border:'none', borderRadius:14, fontSize:15, fontWeight:600, cursor:isLoading?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:12, boxShadow:isLoading?'none':'0 4px 12px rgba(0,0,0,.15)' }}>
            {isLoading ? <><RefreshCw size={20} className="animate-spin"/> Conectando...</> : <>
              <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continuar con Google
            </>}
          </button>
          <div id="g-btn" style={{ marginTop:12, display:'flex', justifyContent:'center' }}/>
          <div style={{ display:'flex', alignItems:'center', gap:16, margin:'28px 0' }}>
            <div style={{ flex:1, height:1, background:'rgba(148,163,184,.15)' }}/><span style={{ fontSize:12, color:'#475569', textTransform:'uppercase', letterSpacing:1 }}>Info</span><div style={{ flex:1, height:1, background:'rgba(148,163,184,.15)' }}/>
          </div>
          {[{ icon:'📊', t:'Lectura y escritura en Google Sheets' },{ icon:'📁', t:'Comprobantes guardados en tu Google Drive' },{ icon:'🔒', t:'Solo accede a archivos creados por FondApp' }].map((x,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:10, background:'rgba(148,163,184,.05)', marginBottom:8 }}><span style={{ fontSize:18 }}>{x.icon}</span><span style={{ fontSize:13, color:'#94a3b8' }}>{x.t}</span></div>
          ))}
        </div>
        <p style={{ textAlign:'center', fontSize:12, color:'#475569', marginTop:32 }}>FondApp v2.0</p>
        <div style={{ textAlign:'center', marginTop:12, display:'flex', justifyContent:'center', gap:8, alignItems:'center' }}>
          <a href="/privacidad" target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#6366f1', textDecoration:'none', padding:'4px 10px', borderRadius:6, border:'1px solid rgba(99,102,241,.25)', transition:'all 0.2s' }}
            onMouseEnter={e => { e.target.style.background='rgba(99,102,241,.1)'; }}
            onMouseLeave={e => { e.target.style.background='transparent'; }}
          >🔒 Política de Privacidad</a>
          <span style={{ color:'#334155', fontSize:10 }}>•</span>
          <a href="/condiciones" target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#6366f1', textDecoration:'none', padding:'4px 10px', borderRadius:6, border:'1px solid rgba(99,102,241,.25)', transition:'all 0.2s' }}
            onMouseEnter={e => { e.target.style.background='rgba(99,102,241,.1)'; }}
            onMouseLeave={e => { e.target.style.background='transparent'; }}
          >📋 Términos y Condiciones</a>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   INVENTORY MODULE
   ═══════════════════════════════════════════ */

// Constants OUTSIDE any component — never re-created
const _iStyle = {width:'100%',padding:'10px 14px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14,boxSizing:'border-box'};
const _sStyle = {width:'100%',padding:'10px 14px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14,boxSizing:'border-box'};
const _lStyle = {display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:6};
const INV_CATS = { equipamiento:{l:'🔬 Equip. Científico',c:'#6366f1'}, informatica:{l:'💻 Informática',c:'#3b82f6'}, oficina:{l:'🗄️ Mobiliario',c:'#10b981'}, insumos:{l:'🧪 Insumos',c:'#f59e0b'}, vehiculos:{l:'🚗 Vehículos',c:'#ef4444'}, otros:{l:'📦 Otros',c:'#8b5cf6'} };
const INV_STATS = { disponible:{l:'Disponible',c:'#10b981'}, en_uso:{l:'En Uso',c:'#3b82f6'}, mantencion:{l:'Mantención',c:'#f59e0b'}, dado_baja:{l:'Dado de Baja',c:'#ef4444'} };
const EMPTY_ITEM = { name:'', code:'', category:'equipamiento', description:'', quantity:1, unit:'unidad', location:'', minStock:0, acquisitionDate:new Date().toISOString().split('T')[0], acquisitionCost:'', supplier:'', status:'disponible' };
const EMPTY_MOV = { type:'entrada', quantity:1, reason:'', responsible:'', date:new Date().toISOString().split('T')[0] };

// ─── Separate Add Form Component ─────────────────────────────
const AddItemForm = ({ onAdd, onCancel, codeHint }) => {
  const [f, setF] = useState({...EMPTY_ITEM});
  const u = (k, v) => setF(p => ({...p, [k]: v}));

  return (
    <div>
      <h3 style={{fontSize:20,fontWeight:700,marginBottom:24}}>➕ Agregar al Inventario</h3>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        <div style={{gridColumn:'span 2'}}><label style={_lStyle}>Nombre *</label><input value={f.name} onChange={e=>u('name',e.target.value)} placeholder="Ej: Microscopio Digital" style={_iStyle}/></div>
        <div><label style={_lStyle}>Código (auto si vacío)</label><input value={f.code} onChange={e=>u('code',e.target.value)} placeholder={codeHint} style={_iStyle}/></div>
        <div><label style={_lStyle}>Categoría *</label><select value={f.category} onChange={e=>u('category',e.target.value)} style={_sStyle}>{Object.entries(INV_CATS).map(([k,v])=><option key={k} value={k}>{v.l}</option>)}</select></div>
        <div><label style={_lStyle}>Cantidad</label><input type="number" value={f.quantity} min="0" onChange={e=>u('quantity',parseInt(e.target.value)||0)} style={_iStyle}/></div>
        <div><label style={_lStyle}>Unidad</label><select value={f.unit} onChange={e=>u('unit',e.target.value)} style={_sStyle}>{['unidad','kit','caja','litro','kg','metro','rollo'].map(x=><option key={x} value={x}>{x.charAt(0).toUpperCase()+x.slice(1)}</option>)}</select></div>
        <div><label style={_lStyle}>Stock Mínimo</label><input type="number" value={f.minStock} min="0" onChange={e=>u('minStock',parseInt(e.target.value)||0)} style={_iStyle}/></div>
        <div><label style={_lStyle}>Ubicación</label><input value={f.location} onChange={e=>u('location',e.target.value)} placeholder="Ej: Lab 3" style={_iStyle}/></div>
        <div><label style={_lStyle}>Fecha Adquisición</label><input type="date" value={f.acquisitionDate} onChange={e=>u('acquisitionDate',e.target.value)} style={_iStyle}/></div>
        <div><label style={_lStyle}>Costo Unitario ($)</label><input type="number" value={f.acquisitionCost} min="0" step="0.01" onChange={e=>u('acquisitionCost',e.target.value)} placeholder="0.00" style={_iStyle}/></div>
        <div><label style={_lStyle}>Proveedor</label><input value={f.supplier} onChange={e=>u('supplier',e.target.value)} style={_iStyle}/></div>
        <div><label style={_lStyle}>Estado</label><select value={f.status} onChange={e=>u('status',e.target.value)} style={_sStyle}>{Object.entries(INV_STATS).map(([k,v])=><option key={k} value={k}>{v.l}</option>)}</select></div>
        <div style={{gridColumn:'span 2'}}><label style={_lStyle}>Descripción</label><textarea value={f.description} onChange={e=>u('description',e.target.value)} placeholder="Detalles, modelo, serie..." rows="2" style={{..._iStyle,resize:'vertical'}}/></div>
      </div>
      <div style={{display:'flex',gap:12,marginTop:24,justifyContent:'flex-end'}}>
        <button onClick={onCancel} style={{padding:'10px 24px',borderRadius:10,border:'1px solid #e2e8f0',background:'white',fontSize:14,cursor:'pointer',color:'#64748b'}}>Cancelar</button>
        <button onClick={()=>{ if(!f.name){alert('⚠️ Ingresa el nombre');return;} onAdd(f); }} style={{padding:'10px 24px',borderRadius:10,border:'none',background:'#6366f1',color:'white',fontSize:14,fontWeight:600,cursor:'pointer'}}>Agregar</button>
      </div>
    </div>
  );
};

// ─── Separate Movement Form Component ────────────────────────
const MovementForm = ({ item, onSubmit, onCancel }) => {
  const [m, setM] = useState({...EMPTY_MOV});
  const u = (k, v) => setM(p => ({...p, [k]: v}));

  return (
    <div>
      <h3 style={{fontSize:18,fontWeight:700,marginBottom:6}}>📦 Registrar Movimiento</h3>
      <p style={{fontSize:14,color:'#64748b',marginBottom:20}}>{item.name} — Stock: {item.quantity} {item.unit}</p>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div><label style={_lStyle}>Tipo</label><select value={m.type} onChange={e=>u('type',e.target.value)} style={_sStyle}><option value="entrada">📥 Entrada</option><option value="salida">📤 Salida</option></select></div>
        <div><label style={_lStyle}>Cantidad</label><input type="number" value={m.quantity} min="1" onChange={e=>u('quantity',parseInt(e.target.value)||0)} style={_iStyle}/></div>
        <div><label style={_lStyle}>Motivo</label><input value={m.reason} onChange={e=>u('reason',e.target.value)} placeholder="Compra, préstamo, consumo..." style={_iStyle}/></div>
        <div><label style={_lStyle}>Responsable</label><input value={m.responsible} onChange={e=>u('responsible',e.target.value)} placeholder="Nombre del responsable" style={_iStyle}/></div>
      </div>
      <div style={{display:'flex',gap:12,marginTop:24,justifyContent:'flex-end'}}>
        <button onClick={onCancel} style={{padding:'10px 24px',borderRadius:10,border:'1px solid #e2e8f0',background:'white',fontSize:14,cursor:'pointer',color:'#64748b'}}>Cancelar</button>
        <button onClick={()=>{ if(m.quantity<=0){alert('⚠️ Cantidad > 0');return;} if(m.type==='salida'&&m.quantity>item.quantity){alert('⚠️ Stock insuficiente');return;} onSubmit(m); }} style={{padding:'10px 24px',borderRadius:10,border:'none',background:m.type==='entrada'?'#10b981':'#f59e0b',color:'white',fontSize:14,fontWeight:600,cursor:'pointer'}}>Registrar</button>
      </div>
    </div>
  );
};

// ─── Main Inventory Component (receives data via props) ──────
const InventoryModule = ({ inventory, setInventory, movements, setMovements }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [showMov, setShowMov] = useState(null);
  const [view, setView] = useState('list');

  const genCode = () => `INV-${String(inventory.length+1).padStart(4,'0')}`;

  const handleAddItem = async (newItem) => {
    const item = { id:Date.now(), ...newItem, code:newItem.code||genCode(), acquisitionCost:parseFloat(newItem.acquisitionCost)||0, createdAt:new Date().toLocaleString('es-ES') };
    const mov = { id:Date.now()+1, itemId:item.id, itemName:item.name, itemCode:item.code, type:'entrada', quantity:item.quantity, reason:'Alta de inventario', responsible:'Sistema', date:newItem.acquisitionDate, timestamp:new Date().toLocaleString('es-ES') };
    setInventory(p=>[item,...p]);
    setMovements(p=>[mov,...p]);
    setShowAdd(false);
    // Sync to Google Sheets
    const [s1, s2] = await Promise.all([GS.saveInventoryItem(item), GS.saveMovement(mov)]);
    alert(s1 ? '✅ Artículo guardado en Google Sheets' : '✅ Artículo guardado localmente');
  };

  const handleMovement = async (movData) => {
    const item = showMov;
    const updatedQty = movData.type==='entrada' ? item.quantity+movData.quantity : item.quantity-movData.quantity;
    const updatedItem = {...item, quantity: updatedQty};
    const mov = {id:Date.now(), itemId:item.id, itemName:item.name, itemCode:item.code, ...movData, timestamp:new Date().toLocaleString('es-ES')};
    setInventory(p=>p.map(i=>i.id===item.id ? updatedItem : i));
    setMovements(p=>[mov,...p]);
    setShowMov(null);
    await Promise.all([GS.updateInventoryItem(updatedItem), GS.saveMovement(mov)]);
  };

  const deleteItem = async (id) => {
    if(!window.confirm('¿Eliminar artículo?'))return;
    const item = inventory.find(i=>i.id===id);
    const mov = {id:Date.now(), itemId:id, itemName:item?.name||'', itemCode:item?.code||'', type:'baja', quantity:item?.quantity||0, reason:'Eliminación', responsible:'Sistema', date:new Date().toISOString().split('T')[0], timestamp:new Date().toLocaleString('es-ES')};
    setInventory(p=>p.filter(i=>i.id!==id));
    setMovements(p=>[mov,...p]);
    await Promise.all([GS.deleteInventoryItem(id), GS.saveMovement(mov)]);
  };

  const filtered = inventory.filter(i => {
    const ms = i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.code.toLowerCase().includes(searchTerm.toLowerCase());
    return ms && (filterCat==='all'||i.category===filterCat);
  });

  const totalVal = inventory.reduce((a,i)=>a+(i.acquisitionCost*i.quantity),0);
  const lowStock = inventory.filter(i=>i.minStock>0&&i.quantity<=i.minStock);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {label:'Artículos',val:inventory.length,sub:`${inventory.reduce((a,i)=>a+i.quantity,0)} unidades`,bg:'linear-gradient(135deg,#6366f1,#818cf8)'},
          {label:'Valor Total',val:`$${totalVal.toLocaleString()}`,bg:'linear-gradient(135deg,#10b981,#34d399)'},
          {label:'Movimientos',val:movements.length,bg:'linear-gradient(135deg,#f59e0b,#fbbf24)'},
          {label:'Stock Bajo',val:lowStock.length,sub:lowStock.length?'⚠️ Atención':'✅ OK',bg:lowStock.length?'linear-gradient(135deg,#ef4444,#f87171)':'linear-gradient(135deg,#64748b,#94a3b8)'}
        ].map((s,i)=>(
          <div key={i} style={{background:s.bg,borderRadius:12,padding:20,color:'white'}}>
            <div style={{fontSize:13,opacity:.85}}>{s.label}</div>
            <div style={{fontSize:26,fontWeight:800,marginTop:4}}>{s.val}</div>
            {s.sub&&<div style={{fontSize:12,opacity:.7,marginTop:2}}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{display:'flex',flexWrap:'wrap',gap:12,alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',flex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:8,background:'#f1f5f9',borderRadius:10,padding:'8px 14px',flex:1,minWidth:200}}>
            <Search size={18} color="#64748b"/>
            <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Buscar..." style={{border:'none',background:'transparent',outline:'none',fontSize:14,width:'100%',color:'#334155'}}/>
          </div>
          <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{padding:'8px 14px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14}}>
            <option value="all">Todas</option>
            {Object.entries(INV_CATS).map(([k,v])=><option key={k} value={k}>{v.l}</option>)}
          </select>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={()=>setView(view==='list'?'movements':'list')} style={{padding:'8px 16px',borderRadius:10,border:'1px solid #e2e8f0',background:'white',fontSize:14,cursor:'pointer',display:'flex',alignItems:'center',gap:6,color:'#334155'}}>
            {view==='list'?<><Archive size={16}/> Movimientos</>:<><Package size={16}/> Inventario</>}
          </button>
          <button onClick={()=>setShowAdd(true)} style={{padding:'8px 20px',borderRadius:10,border:'none',background:'#6366f1',color:'white',fontSize:14,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
            <Plus size={16}/> Agregar
          </button>
        </div>
      </div>

      {/* Low stock alert */}
      {lowStock.length>0&&(
        <div style={{background:'rgba(239,68,68,.05)',border:'1px solid rgba(239,68,68,.2)',borderRadius:12,padding:16}}>
          <h4 style={{color:'#dc2626',fontWeight:700,fontSize:14,marginBottom:8}}>⚠️ Alertas de Stock Bajo</h4>
          <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
            {lowStock.map(i=><span key={i.id} style={{background:'rgba(239,68,68,.1)',padding:'4px 12px',borderRadius:20,fontSize:13,color:'#b91c1c'}}>{i.name}: {i.quantity}/{i.minStock}</span>)}
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAdd&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div style={{background:'white',borderRadius:20,padding:32,width:'100%',maxWidth:700,maxHeight:'85vh',overflowY:'auto'}}>
            <AddItemForm onAdd={handleAddItem} onCancel={()=>setShowAdd(false)} codeHint={genCode()} />
          </div>
        </div>
      )}

      {/* Movement Modal */}
      {showMov&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div style={{background:'white',borderRadius:20,padding:32,width:'100%',maxWidth:450,maxHeight:'85vh',overflowY:'auto'}}>
            <MovementForm item={showMov} onSubmit={handleMovement} onCancel={()=>setShowMov(null)} />
          </div>
        </div>
      )}

      {/* List / Movements */}
      {view==='list'?(
        filtered.length===0?(
          <div style={{textAlign:'center',padding:60,background:'#f8fafc',borderRadius:16}}>
            <Package size={48} color="#cbd5e1" style={{margin:'0 auto 16px'}}/>
            <p style={{color:'#94a3b8',fontSize:16}}>{inventory.length===0?'No hay artículos':'Sin resultados'}</p>
          </div>
        ):(
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {filtered.map(item=>{
              const cat=INV_CATS[item.category]||{l:'Otro',c:'#64748b'};
              const st=INV_STATS[item.status]||{l:'—',c:'#64748b'};
              const low=item.minStock>0&&item.quantity<=item.minStock;
              return(
                <div key={item.id} style={{background:'white',borderRadius:14,border:low?'2px solid #fbbf24':'1px solid #e2e8f0',padding:'18px 20px'}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
                    <div style={{flex:1,minWidth:200}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                        <span style={{background:cat.c,color:'white',padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:600}}>{item.code}</span>
                        <span style={{background:`${st.c}15`,color:st.c,padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:600}}>{st.l}</span>
                        {low&&<span style={{background:'rgba(239,68,68,.1)',color:'#dc2626',padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:600}}>⚠️ Stock Bajo</span>}
                      </div>
                      <h4 style={{fontSize:16,fontWeight:700,color:'#1e293b',margin:'0 0 4px'}}>{item.name}</h4>
                      <div style={{display:'flex',flexWrap:'wrap',gap:16,fontSize:13,color:'#64748b'}}>
                        <span>📦 {item.quantity} {item.unit}</span>
                        {item.location&&<span>📍 {item.location}</span>}
                        {item.acquisitionCost>0&&<span>💰 ${item.acquisitionCost.toLocaleString()}/u</span>}
                        {item.supplier&&<span>🏪 {item.supplier}</span>}
                      </div>
                      {item.description&&<p style={{fontSize:13,color:'#94a3b8',marginTop:6}}>{item.description}</p>}
                    </div>
                    <div style={{display:'flex',gap:6}}>
                      <button onClick={()=>setShowMov(item)} style={{padding:'8px 14px',borderRadius:8,border:'1px solid #e2e8f0',background:'white',fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                        <RefreshCw size={14}/> Movimiento
                      </button>
                      <button onClick={()=>deleteItem(item.id)} style={{padding:8,borderRadius:8,border:'1px solid #fecaca',background:'rgba(239,68,68,.05)',cursor:'pointer',color:'#ef4444',display:'flex',alignItems:'center'}}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ):(
        <div>
          <h3 style={{fontSize:18,fontWeight:700,color:'#1e293b',marginBottom:16}}>📋 Historial de Movimientos</h3>
          {movements.length===0?(
            <div style={{textAlign:'center',padding:40,background:'#f8fafc',borderRadius:16}}><p style={{color:'#94a3b8'}}>Sin movimientos</p></div>
          ):(
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead><tr style={{background:'#f1f5f9'}}>
                  {['Fecha','Tipo','Artículo','Cant.','Motivo','Responsable'].map(h=><th key={h} style={{padding:'10px 14px',textAlign:'left',fontWeight:600,color:'#475569'}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {movements.map(m=>(
                    <tr key={m.id} style={{borderBottom:'1px solid #f1f5f9'}}>
                      <td style={{padding:'10px 14px'}}>{m.date}</td>
                      <td style={{padding:'10px 14px'}}>
                        <span style={{padding:'2px 10px',borderRadius:20,fontSize:12,fontWeight:600,background:m.type==='entrada'?'rgba(16,185,129,.1)':m.type==='salida'?'rgba(245,158,11,.1)':'rgba(239,68,68,.1)',color:m.type==='entrada'?'#059669':m.type==='salida'?'#d97706':'#dc2626'}}>
                          {m.type==='entrada'?'📥 Entrada':m.type==='salida'?'📤 Salida':'🗑️ Baja'}
                        </span>
                      </td>
                      <td style={{padding:'10px 14px'}}>{m.itemCode} — {m.itemName}</td>
                      <td style={{padding:'10px 14px',textAlign:'center',fontWeight:600}}>{m.quantity}</td>
                      <td style={{padding:'10px 14px',color:'#64748b'}}>{m.reason||'—'}</td>
                      <td style={{padding:'10px 14px',color:'#64748b'}}>{m.responsible||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════ */
const FondApp = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('instrucciones');
  const [expenses, setExpenses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [movements, setMovements] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  
  const [budgets, setBudgets] = useState({
    'Gastos de Operación': { limitInstitucion:0, limitContraparte:0, spent:0 },
    'Recursos Humanos': { limitInstitucion:0, limitContraparte:0, spent:0 },
    'Gastos de Inversión': { limitInstitucion:0, limitContraparte:0, spent:0 },
    'Gastos de Administración': { limitInstitucion:0, limitContraparte:0, spent:0 },
    'Overhead': { limitInstitucion:0, limitContraparte:0, spent:0 }
  });

  const [newExpense, setNewExpense] = useState({
    category:'Gastos de Operación', description:'', amount:'',
    date:new Date().toISOString().split('T')[0],
    boletaFactura:null, boletaFacturaName:'', boletaFacturaType:'boleta',
    comprobantePago:null, comprobantePagoName:''
  });

  const [newMilestone, setNewMilestone] = useState({ type:'informe', title:'', date:'', description:'', status:'pendiente' });

  const [budgetSetup, setBudgetSetup] = useState({
    'Gastos de Operación': { institucion:'', contraparte:'' },
    'Recursos Humanos': { institucion:'', contraparte:'' },
    'Gastos de Inversión': { institucion:'', contraparte:'' },
    'Gastos de Administración': { institucion:'', contraparte:'' },
    'Overhead': { institucion:'', contraparte:'' }
  });

  // ─── Google Sync via googleService ─────────────────────────
  const loadFromGoogle = async () => {
    if (!GS.isAuthenticated()) { setIsConnected(false); return; }
    setIsConnected(true);
    setIsSyncing(true);
    try {
      const data = await GS.loadAllData();
      if (data.success) {
        if (data.budgets && Object.keys(data.budgets).length > 0) setBudgets(prev => ({...prev, ...data.budgets}));
        if (data.expenses?.length) setExpenses(data.expenses);
        if (data.alerts?.length) setAlerts(data.alerts);
        if (data.milestones?.length) setMilestones(data.milestones.sort((a,b) => new Date(a.date) - new Date(b.date)));
        if (data.inventory?.length) setInventory(data.inventory);
        if (data.movements?.length) setMovements(data.movements);
        setLastSync(new Date().toLocaleTimeString('es-ES'));
      }
    } catch (e) { console.error('Error cargando:', e); }
    finally { setIsSyncing(false); }
  };

  useEffect(() => { if (user && user.token !== 'demo') { loadFromGoogle(); } }, [user]);

  const handleLogout = () => {
    GS.logout();
    setUser(null);
    setIsConnected(false);
  };

  // Budget
  const handleBudgetSetup = async () => {
    const nb = {...budgets}; let has=false;
    Object.keys(budgetSetup).forEach(c => {
      const i=parseFloat(budgetSetup[c].institucion), cp=parseFloat(budgetSetup[c].contraparte);
      if(!isNaN(i)&&i>0){nb[c].limitInstitucion=i;has=true;} if(!isNaN(cp)&&cp>0){nb[c].limitContraparte=cp;has=true;}
    });
    if(!has){alert('⚠️ Ingresa al menos un presupuesto');return;}
    setBudgets(nb); setIsSyncing(true);
    const saved = await GS.saveBudgets(nb);
    setIsSyncing(false); alert(saved?'✅ Guardado en Google Sheets':'⚠️ Guardado localmente');
  };

  // Milestones
  const handleAddMilestone = async () => {
    if(!newMilestone.title||!newMilestone.date){alert('⚠️ Completa título y fecha');return;}
    const m = {id:Date.now(),...newMilestone,createdAt:new Date().toLocaleString('es-ES')};
    setMilestones(p=>[...p,m].sort((a,b)=>new Date(a.date)-new Date(b.date)));
    setIsSyncing(true); const saved = await GS.saveMilestone(m); setIsSyncing(false);
    setNewMilestone({type:'informe',title:'',date:'',description:'',status:'pendiente'});
    alert(saved?'✅ Hito guardado en Google Sheets':'✅ Hito guardado localmente');
  };

  const updateMilestoneStatus = async (id, st) => {
    setMilestones(p=>p.map(m=>m.id===id?{...m,status:st}:m));
    await GS.updateMilestoneStatus(id, st);
  };

  const getMilestoneIcon = t => ({'informe':'📄','rendicion':'💰','reitimizacion':'🔄'}[t]||'📌');
  const getMilestoneColor = s => ({'pendiente':'border-yellow-300 bg-yellow-50','en_proceso':'border-blue-300 bg-blue-50','completado':'border-green-300 bg-green-50','vencido':'border-red-300 bg-red-50'}[s]||'border-gray-300 bg-gray-50');

  const handleAddExpense = async () => {
    if(!newExpense.description||!newExpense.amount||parseFloat(newExpense.amount)<=0){alert('Completa los campos obligatorios');return;}
    if(!newExpense.boletaFactura){alert('⚠️ Adjunta la boleta o factura');return;}
    if(!newExpense.comprobantePago){alert('⚠️ Adjunta el comprobante de pago');return;}
    const amt=parseFloat(newExpense.amount);
    const exp={id:Date.now(),...newExpense,amount:amt,timestamp:new Date().toLocaleString('es-ES')};
    const nb={...budgets}; nb[newExpense.category].spent+=amt; setBudgets(nb);
    
    // Check budget alerts
    const b=nb[newExpense.category], lim=b.limitInstitucion+b.limitContraparte, pct=lim>0?(b.spent/lim)*100:0;
    let alertMsg=null, alertType='info';
    if(pct>=100){alertMsg=`ALERTA: ${newExpense.category} SUPERÓ el presupuesto (${pct.toFixed(1)}%)`;alertType='danger';}
    else if(pct>=90){alertMsg=`ADVERTENCIA: ${newExpense.category} al ${pct.toFixed(1)}%`;alertType='warning';}
    else if(pct>=80){alertMsg=`ATENCIÓN: ${newExpense.category} al ${pct.toFixed(1)}%`;alertType='info';}
    if(alertMsg){
      const newAlert={id:Date.now(),message:alertMsg,type:alertType,category:newExpense.category,timestamp:new Date().toLocaleString('es-ES')};
      setAlerts(p=>[newAlert,...p].slice(0,20));
      GS.saveAlert(newAlert);
    }

    setExpenses(p=>[exp,...p]);
    setIsSyncing(true);
    // Save expense (files are uploaded to Drive inside saveExpense)
    const saved = await GS.saveExpense(exp);
    // Also update budgets in Sheets
    await GS.saveBudgets(nb);
    setIsSyncing(false);

    setNewExpense({category:'Gastos de Operación',description:'',amount:'',date:new Date().toISOString().split('T')[0],boletaFactura:null,boletaFacturaName:'',boletaFacturaType:'boleta',comprobantePago:null,comprobantePagoName:''});
    alert(saved?'✅ Gasto registrado y archivos subidos a Google Drive':'✅ Gasto registrado localmente');
  };

  const handleFileChange = (e, type) => {
    const f=e.target.files[0]; if(!f)return;
    if(f.size>5*1024*1024){alert('Archivo < 5MB');return;}
    const r=new FileReader();
    r.onloadend=()=>{
      if(type==='boletaFactura') setNewExpense(p=>({...p,boletaFactura:r.result,boletaFacturaName:f.name}));
      else setNewExpense(p=>({...p,comprobantePago:r.result,comprobantePagoName:f.name}));
    };
    r.readAsDataURL(f);
  };

  const getCategoryColor = c => ({'Gastos de Operación':'bg-blue-500','Recursos Humanos':'bg-green-500','Gastos de Inversión':'bg-purple-500','Gastos de Administración':'bg-yellow-500','Overhead':'bg-red-500'}[c]||'bg-gray-500');
  const getAlertColor = t => ({'danger':'bg-red-100 border-red-500 text-red-800','warning':'bg-yellow-100 border-yellow-500 text-yellow-800','info':'bg-blue-100 border-blue-500 text-blue-800'}[t]||'bg-gray-100');

  const tBI = Object.values(budgets).reduce((a,b)=>a+b.limitInstitucion,0);
  const tBC = Object.values(budgets).reduce((a,b)=>a+b.limitContraparte,0);
  const tB = tBI+tBC;
  const tS = Object.values(budgets).reduce((a,b)=>a+b.spent,0);
  const tR = tB-tS;

  // ===== AUTH GATE =====
  if(!user) return <LoginScreen onLogin={setUser}/>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3"><DollarSign className="text-indigo-600"/> FondApp</h1>
              <p className="text-gray-600 mt-2">Sistema de Gestión de Fondos Concursables</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-3 mb-3 justify-end">
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-800">{user.name}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
                {user.picture?<img src={user.picture} alt="" style={{width:36,height:36,borderRadius:'50%',border:'2px solid #e2e8f0'}}/>:
                <div style={{width:36,height:36,borderRadius:'50%',background:'#6366f1',display:'flex',alignItems:'center',justifyContent:'center'}}><User size={18} color="white"/></div>}
                <button onClick={handleLogout} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Cerrar Sesión"><LogOut size={18}/></button>
              </div>
              <div className="flex items-center gap-2 mb-2 justify-end">
                {isConnected?<div className="flex items-center gap-2 text-green-600 text-sm"><Cloud size={16}/><span>Sheets & Drive</span></div>:<div className="flex items-center gap-2 text-yellow-600 text-sm"><CloudOff size={16}/><span>Modo Local</span></div>}
                <button onClick={loadFromGoogle} disabled={isSyncing} className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"><RefreshCw size={16} className={isSyncing?'animate-spin':''}/></button>
              </div>
              {lastSync&&<div className="text-xs text-gray-500 mb-2">Sync: {lastSync}</div>}
              <div className="text-sm text-gray-500">Presupuesto Global</div>
              <div className="text-2xl font-bold text-indigo-600">${tB.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">Inst: ${tBI.toLocaleString()} | Contr: ${tBC.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mt-1">Disponible: ${tR.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="bg-white rounded-lg shadow-lg mb-6">
          <div className="flex border-b overflow-x-auto">
            {['instrucciones','hitos','presupuesto','registro','dashboard','alertas','reportes','inventario'].map(tab=>(
              <button key={tab} onClick={()=>setActiveTab(tab)} className={`flex-1 py-4 px-3 font-semibold transition-colors whitespace-nowrap text-sm ${activeTab===tab?'bg-indigo-600 text-white border-b-4 border-indigo-800':'text-gray-600 hover:bg-gray-50'}`}>
                {{instrucciones:'📖 Instrucciones',hitos:'📅 Hitos',presupuesto:'💰 Presupuesto',registro:'📝 Registrar',dashboard:'📊 Dashboard',alertas:`🔔 Alertas (${alerts.length})`,reportes:'📈 Reportes',inventario:'📦 Inventario'}[tab]}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* INSTRUCCIONES */}
            {activeTab==='instrucciones'&&(
              <div className="space-y-6 max-w-4xl">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-lg">
                  <h2 className="text-2xl font-bold mb-2">Bienvenido a FondApp</h2>
                  <p className="text-indigo-100">Sistema de Gestión de Fondos Concursables</p>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h4 className="font-bold text-blue-900 mb-2">📖 Guía de Uso</h4>
                  <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
                    <li><strong>Hitos:</strong> Registra fechas clave del proyecto</li>
                    <li><strong>Presupuesto:</strong> Configura aportes institución y contraparte</li>
                    <li><strong>Registrar:</strong> Documenta gastos con boleta/factura + comprobante de pago</li>
                    <li><strong>Dashboard:</strong> Monitorea estado presupuestario</li>
                    <li><strong>Alertas:</strong> Notificaciones de límites</li>
                    <li><strong>Reportes:</strong> Informes para rendiciones</li>
                    <li><strong>Inventario:</strong> Administra activos y materiales del proyecto</li>
                  </ol>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-2">💡 Conceptos Clave</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li><strong>Boleta/Factura:</strong> Documento tributario que respalda la compra</li>
                    <li><strong>Comprobante de Pago:</strong> Documento que acredita la transferencia o pago</li>
                    <li><strong>Reitimización:</strong> Reorganización presupuestaria entre categorías</li>
                    <li><strong>Rendición:</strong> Presentación formal de comprobantes</li>
                  </ul>
                </div>
              </div>
            )}

            {/* HITOS */}
            {activeTab==='hitos'&&(
              <div className="space-y-6">
                <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4"><p className="text-indigo-800 text-sm"><strong>Hitos Científicos:</strong> Registra fechas importantes del proyecto.</p></div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">➕ Agregar Hito</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-semibold text-gray-700 mb-2">Tipo</label><select value={newMilestone.type} onChange={e=>setNewMilestone(p=>({...p,type:e.target.value}))} className="w-full p-2 border border-gray-300 rounded-lg"><option value="informe">📄 Informe</option><option value="rendicion">💰 Rendición</option><option value="reitimizacion">🔄 Reitimización</option></select></div>
                    <div><label className="block text-sm font-semibold text-gray-700 mb-2">Fecha</label><input type="date" value={newMilestone.date} onChange={e=>setNewMilestone(p=>({...p,date:e.target.value}))} className="w-full p-2 border border-gray-300 rounded-lg"/></div>
                    <div className="md:col-span-2"><label className="block text-sm font-semibold text-gray-700 mb-2">Título</label><input type="text" value={newMilestone.title} onChange={e=>setNewMilestone(p=>({...p,title:e.target.value}))} placeholder="Ej: Informe Semestral" className="w-full p-2 border border-gray-300 rounded-lg"/></div>
                    <div className="md:col-span-2"><label className="block text-sm font-semibold text-gray-700 mb-2">Descripción</label><textarea value={newMilestone.description} onChange={e=>setNewMilestone(p=>({...p,description:e.target.value}))} className="w-full p-2 border border-gray-300 rounded-lg" rows="2"/></div>
                  </div>
                  <button onClick={handleAddMilestone} className="mt-4 bg-indigo-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-indigo-700">Agregar</button>
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-800">📋 Hitos Registrados</h3>
                  {milestones.length===0?<div className="text-center py-12 bg-gray-50 rounded-lg"><p className="text-gray-500">No hay hitos registrados</p></div>:
                  milestones.map(m=>(
                    <div key={m.id} className={`border-2 rounded-lg p-4 ${getMilestoneColor(m.status)}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="text-2xl">{getMilestoneIcon(m.type)}</div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-800">{m.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">📅 {new Date(m.date).toLocaleDateString('es-ES',{year:'numeric',month:'long',day:'numeric'})}</p>
                            {m.description&&<p className="text-sm text-gray-600 mt-2">{m.description}</p>}
                          </div>
                        </div>
                        <select value={m.status} onChange={e=>updateMilestoneStatus(m.id,e.target.value)} className="text-sm p-1 border border-gray-300 rounded ml-4">
                          <option value="pendiente">⏳ Pendiente</option><option value="en_proceso">🔄 En Proceso</option><option value="completado">✅ Completado</option><option value="vencido">❌ Vencido</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PRESUPUESTO */}
            {activeTab==='presupuesto'&&(
              <div className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4"><p className="text-blue-800 text-sm"><strong>Presupuesto por Fuente</strong></p></div>
                <div className="grid grid-cols-1 gap-6">
                  {Object.keys(budgets).map(cat=>(
                    <div key={cat} className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                      <h4 className="font-bold text-gray-800 mb-3">{cat}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-sm font-semibold text-blue-700 mb-2">💼 Institución</label><div className="flex items-center gap-2"><span>$</span><input type="number" value={budgetSetup[cat].institucion} onChange={e=>setBudgetSetup(p=>({...p,[cat]:{...p[cat],institucion:e.target.value}}))} placeholder="0.00" className="flex-1 p-2 border border-gray-300 rounded-lg"/></div>{budgets[cat].limitInstitucion>0&&<div className="mt-1 text-xs text-gray-600">Actual: ${budgets[cat].limitInstitucion.toLocaleString()}</div>}</div>
                        <div><label className="block text-sm font-semibold text-green-700 mb-2">🏢 Contraparte</label><div className="flex items-center gap-2"><span>$</span><input type="number" value={budgetSetup[cat].contraparte} onChange={e=>setBudgetSetup(p=>({...p,[cat]:{...p[cat],contraparte:e.target.value}}))} placeholder="0.00" className="flex-1 p-2 border border-gray-300 rounded-lg"/></div>{budgets[cat].limitContraparte>0&&<div className="mt-1 text-xs text-gray-600">Actual: ${budgets[cat].limitContraparte.toLocaleString()}</div>}</div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-300"><div className="flex justify-between text-sm"><span className="font-semibold">Total:</span><span className="font-bold text-indigo-600">${(budgets[cat].limitInstitucion+budgets[cat].limitContraparte).toLocaleString()}</span></div></div>
                    </div>
                  ))}
                </div>
                <button onClick={handleBudgetSetup} className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700">Guardar Presupuestos</button>
              </div>
            )}

            {/* REGISTRO (UPDATED: 2 file uploads) */}
            {activeTab==='registro'&&(
              <div className="space-y-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-4"><p className="text-green-800 text-sm">Registra cada gasto con su <strong>boleta o factura</strong> y su <strong>comprobante de pago</strong> por separado.</p></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-semibold text-gray-700 mb-2">Categoría</label><select value={newExpense.category} onChange={e=>setNewExpense(p=>({...p,category:e.target.value}))} className="w-full p-2 border border-gray-300 rounded-lg">{Object.keys(budgets).map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                  <div><label className="block text-sm font-semibold text-gray-700 mb-2">Fecha</label><input type="date" value={newExpense.date} onChange={e=>setNewExpense(p=>({...p,date:e.target.value}))} className="w-full p-2 border border-gray-300 rounded-lg"/></div>
                  <div className="md:col-span-2"><label className="block text-sm font-semibold text-gray-700 mb-2">Descripción</label><input type="text" value={newExpense.description} onChange={e=>setNewExpense(p=>({...p,description:e.target.value}))} placeholder="Ej: Compra de material" className="w-full p-2 border border-gray-300 rounded-lg"/></div>
                  <div><label className="block text-sm font-semibold text-gray-700 mb-2">Monto</label><input type="number" value={newExpense.amount} onChange={e=>setNewExpense(p=>({...p,amount:e.target.value}))} placeholder="0.00" step="0.01" className="w-full p-2 border border-gray-300 rounded-lg"/></div>
                  <div><label className="block text-sm font-semibold text-gray-700 mb-2">Tipo Documento</label><select value={newExpense.boletaFacturaType} onChange={e=>setNewExpense(p=>({...p,boletaFacturaType:e.target.value}))} className="w-full p-2 border border-gray-300 rounded-lg"><option value="boleta">📄 Boleta</option><option value="factura">📋 Factura</option></select></div>
                </div>

                {/* Two file uploads */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Boleta/Factura */}
                  <div style={{border:newExpense.boletaFactura?'2px solid #10b981':'2px dashed #cbd5e1',borderRadius:12,padding:20,background:newExpense.boletaFactura?'rgba(16,185,129,.03)':'#f8fafc'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                      <span style={{fontSize:20}}>{newExpense.boletaFacturaType==='boleta'?'📄':'📋'}</span>
                      <div><h4 style={{fontSize:14,fontWeight:700,color:'#1e293b',margin:0}}>{newExpense.boletaFacturaType==='boleta'?'Boleta':'Factura'}</h4><p style={{fontSize:12,color:'#64748b',margin:0}}>Documento tributario</p></div>
                    </div>
                    <input type="file" onChange={e=>handleFileChange(e,'boletaFactura')} accept="image/*,.pdf" className="hidden" id="f-boleta"/>
                    <label htmlFor="f-boleta" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:10,borderRadius:8,cursor:'pointer',background:newExpense.boletaFactura?'#10b981':'#6366f1',color:'white',fontSize:13,fontWeight:600}}>
                      <Upload size={16}/> {newExpense.boletaFactura?'Cambiar':'Subir '+(newExpense.boletaFacturaType==='boleta'?'Boleta':'Factura')}
                    </label>
                    {newExpense.boletaFacturaName&&<div style={{marginTop:8,display:'flex',alignItems:'center',gap:6,fontSize:13,color:'#059669'}}><CheckCircle size={14}/> {newExpense.boletaFacturaName}</div>}
                  </div>

                  {/* Comprobante de Pago */}
                  <div style={{border:newExpense.comprobantePago?'2px solid #10b981':'2px dashed #cbd5e1',borderRadius:12,padding:20,background:newExpense.comprobantePago?'rgba(16,185,129,.03)':'#f8fafc'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                      <span style={{fontSize:20}}>💳</span>
                      <div><h4 style={{fontSize:14,fontWeight:700,color:'#1e293b',margin:0}}>Comprobante de Pago</h4><p style={{fontSize:12,color:'#64748b',margin:0}}>Transferencia, voucher o recibo</p></div>
                    </div>
                    <input type="file" onChange={e=>handleFileChange(e,'comprobantePago')} accept="image/*,.pdf" className="hidden" id="f-comp"/>
                    <label htmlFor="f-comp" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:10,borderRadius:8,cursor:'pointer',background:newExpense.comprobantePago?'#10b981':'#6366f1',color:'white',fontSize:13,fontWeight:600}}>
                      <Upload size={16}/> {newExpense.comprobantePago?'Cambiar':'Subir Comprobante'}
                    </label>
                    {newExpense.comprobantePagoName&&<div style={{marginTop:8,display:'flex',alignItems:'center',gap:6,fontSize:13,color:'#059669'}}><CheckCircle size={14}/> {newExpense.comprobantePagoName}</div>}
                  </div>
                </div>

                {/* Status */}
                <div style={{display:'flex',gap:16,padding:'12px 16px',background:'#f1f5f9',borderRadius:10,fontSize:13}}>
                  <span style={{color:newExpense.boletaFactura?'#059669':'#94a3b8'}}>{newExpense.boletaFactura?'✅':'⬜'} {newExpense.boletaFacturaType==='boleta'?'Boleta':'Factura'}</span>
                  <span style={{color:newExpense.comprobantePago?'#059669':'#94a3b8'}}>{newExpense.comprobantePago?'✅':'⬜'} Comprobante de Pago</span>
                </div>

                <button onClick={handleAddExpense} className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700">Registrar Gasto</button>
              </div>
            )}

            {/* DASHBOARD */}
            {activeTab==='dashboard'&&(
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-6 rounded-lg"><div className="text-sm opacity-90">Global</div><div className="text-3xl font-bold mt-2">${tB.toLocaleString()}</div></div>
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg"><div className="text-sm opacity-90">💼 Institución</div><div className="text-2xl font-bold mt-2">${tBI.toLocaleString()}</div></div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg"><div className="text-sm opacity-90">🏢 Contraparte</div><div className="text-2xl font-bold mt-2">${tBC.toLocaleString()}</div></div>
                  <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-lg"><div className="text-sm opacity-90">Gastado</div><div className="text-2xl font-bold mt-2">${tS.toLocaleString()}</div><div className="text-xs mt-1">{tB>0?`${((tS/tB)*100).toFixed(1)}%`:'0%'}</div></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(budgets).map(([cat,d])=>{
                    const lim=d.limitInstitucion+d.limitContraparte, pct=lim>0?(d.spent/lim)*100:0, rem=lim-d.spent;
                    return(
                      <div key={cat} className="bg-white border-2 border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-gray-800 text-sm">{cat}</h3><span className={`px-2 py-1 rounded-full text-xs font-semibold ${pct>=100?'bg-red-100 text-red-800':pct>=80?'bg-yellow-100 text-yellow-800':'bg-green-100 text-green-800'}`}>{pct.toFixed(1)}%</span></div>
                        <div className="space-y-2 text-sm">
                          <div className="bg-blue-50 p-2 rounded"><div className="flex justify-between"><span className="text-blue-700 font-semibold">💼 Institución:</span><span className="font-semibold">${d.limitInstitucion.toLocaleString()}</span></div></div>
                          <div className="bg-green-50 p-2 rounded"><div className="flex justify-between"><span className="text-green-700 font-semibold">🏢 Contraparte:</span><span className="font-semibold">${d.limitContraparte.toLocaleString()}</span></div></div>
                          <div className="border-t pt-2"><div className="flex justify-between"><span>Total:</span><span className="font-bold">${lim.toLocaleString()}</span></div></div>
                          <div className="flex justify-between"><span>Gastado:</span><span className="font-semibold text-red-600">${d.spent.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span>Disponible:</span><span className={`font-semibold ${rem<0?'text-red-600':'text-green-600'}`}>${rem.toLocaleString()}</span></div>
                        </div>
                        <div className="mt-4 bg-gray-200 rounded-full h-2 overflow-hidden"><div className={`h-full ${pct>=100?'bg-red-500':pct>=80?'bg-yellow-500':'bg-green-500'}`} style={{width:`${Math.min(pct,100)}%`}}/></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ALERTAS */}
            {activeTab==='alertas'&&(
              <div className="space-y-4">
                {alerts.length===0?<div className="text-center py-12"><CheckCircle className="mx-auto text-green-500 mb-4" size={48}/><p className="text-gray-600">No hay alertas</p></div>:
                alerts.map(a=><div key={a.id} className={`border-l-4 p-4 rounded-r-lg ${getAlertColor(a.type)}`}><p className="font-semibold text-sm">{a.message}</p><p className="text-xs mt-1 opacity-75">{a.timestamp}</p></div>)}
              </div>
            )}

            {/* REPORTES */}
            {activeTab==='reportes'&&(
              <div className="space-y-6">
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><FileText/> Historial de Gastos</h3>
                  {expenses.length===0?<div className="text-center py-8 text-gray-500">Sin gastos</div>:(
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Fecha</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Categoría</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Descripción</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">Docs</th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">Monto</th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-200">
                          {expenses.map(e=>(
                            <tr key={e.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2">{e.date}</td>
                              <td className="px-3 py-2"><span className={`px-2 py-1 rounded text-xs font-semibold text-white ${getCategoryColor(e.category)}`}>{e.category}</span></td>
                              <td className="px-3 py-2">{e.description}</td>
                              <td className="px-3 py-2 text-center">
                                <span title="Boleta/Factura" style={{marginRight:4}}>{e.boletaFactura?'📄✅':'📄❌'}</span>
                                <span title="Comprobante">{e.comprobantePago?'💳✅':'💳❌'}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-semibold">${e.amount.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-800 mb-4">Resumen</h3>
                  <div className="space-y-3">
                    {Object.entries(budgets).map(([c,d])=><div key={c} className="flex justify-between items-center text-sm"><span>{c}</span><span className="font-semibold">${d.spent.toLocaleString()}</span></div>)}
                    <div className="border-t-2 pt-3 flex justify-between items-center"><span className="font-bold">TOTAL</span><span className="text-xl font-bold text-indigo-600">${tS.toLocaleString()}</span></div>
                  </div>
                </div>
              </div>
            )}
            
            {/* INVENTARIO */}
            {activeTab==='inventario'&&<InventoryModule inventory={inventory} setInventory={setInventory} movements={movements} setMovements={setMovements}/>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FondApp;
